/* ============================================================
   Lettoon — pack/cart helper (window.LtPackCart) + cart drawer runtime
   Owner: Ian (lettoon-website#18, plan Task 3). Replaces Bram's stub wholesale.

   PART 1 — window.LtPackCart (contract C4, consumed by the drawer below AND
   by the builder's edit mode in lt-build-a-pack.js; the public API is locked):
     getPacks()               -> Promise<Pack[]>
     replacePack(token,items) -> Promise<cart>
     removePack(token)        -> Promise<cart>
   plus the `theme:cart:change` document event after every mutation.

   Pack data comes from the api-cart-packs section (Section Rendering API).
   That section is MANDATORY for merged packs: /cart.js does NOT expose
   item_components (live-proven 2026-07-18, #16) — the merged parent only has
   has_components:true there. The property-grouped /cart.js fallback in
   getPacks() therefore only ever describes UNMERGED packs (1-line pack, or
   transform off) and network-failure cases.

   Mutation model (live-proven): the merged parent's line key CHANGES on any
   component mutation, so keys are never cached across mutations — every
   teardown re-fetches the cart first. All cart mutations (pack replace/remove,
   plain line changes, mystery add/remove) run through ONE serialized queue so
   a tap can never race the ~1.2s teardown/re-add/re-merge of a previous tap
   (plan risk #3).

   PART 1.5 — units-aware header cart badge: corrects the Liquid-rendered
   [data-header-cart-count] (item_count, where a merged pack is 1) to the
   real item count, on load and after every cart change; a MutationObserver
   wins over theme.js's Totals rewrites.

   PART 2 — drawer runtime. Renders the items area of sections/cart-drawer
   .liquid client-side from /cart.js + getPacks(), with optimistic quantity
   updates. NEVER renders a quantity stepper on the merged parent line —
   bumping parent qty makes the transform double _lt_units and re-collapse;
   per-item quantity goes through replacePack only.
   ============================================================ */
(function () {
  'use strict';
  // Idempotence guard. The canonical include is sections/cart-drawer.liquid
  // (renders on every non-/cart page via group-overlay); a second <script> tag
  // for the same asset (theme editor duplication, a stale section, a preview
  // harness) executes the file AGAIN, so bail before any listener binds.
  // __ltRuntime marks THIS implementation so a bail-out is diagnosable in the
  // console (vs. silently running on top of Bram's old stub, which set
  // window.LtPackCart without a marker).
  if (window.LtPackCart) {
    if (!window.LtPackCart.__ltRuntime) {
      console.warn('LtPackCart: another implementation already loaded; drawer runtime not started.');
    }
    return;
  }

  var PACK_PROP = '_lt_pack';      // groups a pack; value = pack token
  var ROLE_PROP = '_lt_pack_role'; // 'item' | 'journal' (parent line has NO role)
  var PACKS_SECTION_URL = '/?section_id=api-cart-packs';
  // "Custom Pack" placeholder variant (plan Task 1). The merged parent line
  // carries _lt_pack as a property in /cart.js, so without this filter the
  // fallback grouping would return the parent itself as a bogus 1-item pack —
  // which made the builder's edit-mode preload come up empty and would have
  // wiped a real pack on "Update pack" (Vera, builder QA 2026-07-18).
  var PARENT_VARIANT_ID = '57737765192024';

  /* ---------------------------------------------------------- fetch utils */

  function getJSON(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return {}; }).then(function (json) {
          var err = new Error('http ' + r.status);
          err.status = r.status;
          err.json = json;
          throw err;
        });
      }
      return r.json();
    });
  }

  function postJSON(url, body) {
    return getJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  /* #143 Vera B1 (2026-09-26): cart routes must carry the locale prefix
     (/nl/cart.js returns /nl/products/... line URLs; /cart.js returns English
     ones and the drawer then links a Dutch customer to the English PDP). */
  var LT_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function fetchCart() {
    return getJSON(LT_ROOT + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  }

  function dispatchCartChange(cart) {
    document.dispatchEvent(new CustomEvent('theme:cart:change', {
      detail: { cart: cart, source: 'lt-pack-cart' },
      bubbles: true
    }));
    return cart;
  }

  /* ------------------------------------------------- serialized mutations */

  var mutationChain = Promise.resolve();
  function enqueue(fn) {
    var run = mutationChain.then(fn, fn); // keep the chain alive after failures
    // the chain itself must never reject (or every later call would inherit it)
    mutationChain = run.catch(function () {});
    return run;
  }

  /* -------------------------------------------------------- C4: getPacks */

  // Units per item when the metafield didn't resolve (api-cart-packs emits
  // units:0 then, and /cart.js has no metafields at all). Mirror of the
  // Functions' unitsForLine title heuristic (cart_lines_discounts_generate_run
  // .js) — keep byte-identical; it runs on the VARIANT title ("Both Themes"
  // counts as 2, "All 3 Sticker Sheets" as 3).
  function unitsForTitle(title) {
    title = title || '';
    var num = title.match(/\ball\s+(\d{1,2})\b/i) || title.match(/^(\d{1,2})\s/);
    if (num) return Math.max(1, parseInt(num[1], 10));
    if (/\bboth\b/i.test(title)) return 2;
    if (/ \+ | & /.test(title)) return 2;
    return 1;
  }

  // Normalize a pack's items in place: any item without a resolved units
  // value (>= 1) gets the title heuristic. Applied to BOTH getPacks paths so
  // every consumer (drawer counts/tiers, builder edit mode) sees real units.
  function resolvePackUnits(packs) {
    (packs || []).forEach(function (pack) {
      (pack.items || []).forEach(function (it) {
        if (!(it.units >= 1)) it.units = unitsForTitle(it.variantTitle || it.title);
      });
    });
    return packs;
  }

  // Group a raw /cart.js payload by the _lt_pack property. Only correct for
  // UNMERGED packs — used as the section-failure fallback. A MERGED pack's
  // components are invisible in /cart.js, so a merged pack is simply ABSENT
  // from this result: returning the parent line as a pack would hand callers
  // an empty/bogus items array that replacePack would then persist (wiping
  // the real pack). Callers degrade gracefully on a missing token instead.
  function packsFromCart(cart) {
    var byToken = {};
    var order = [];
    (cart.items || []).forEach(function (line) {
      var props = line.properties || {};
      var token = props[PACK_PROP];
      if (!token) return;
      if (line.has_components || String(line.variant_id) === PARENT_VARIANT_ID) return; // merged parent is never a pack item
      if (!byToken[token]) {
        byToken[token] = { token: token, merged: false, parentKey: line.key, units: 0, packTotal: 0, packOriginal: 0, discount: null, items: [] };
        order.push(token);
      }
      var pack = byToken[token];
      if (props[ROLE_PROP] !== 'journal') {
        pack.units += line.quantity;
        pack.packTotal += line.final_line_price;
        pack.packOriginal += line.original_line_price;
      }
      pack.items.push({
        variantId: line.variant_id || line.id,
        quantity: line.quantity,
        properties: props,
        role: props[ROLE_PROP] || 'item',
        title: line.title,
        name: line.product_title || line.title,
        material: '',
        type: line.product_type || '',
        price: line.final_price,
        linePrice: line.final_line_price,
        originalLinePrice: line.original_line_price,
        image: line.image,
        url: line.url,
        units: unitsForTitle(line.variant_title || line.title),
        options: [],
        variants: []
      });
    });
    return order.map(function (t) { return byToken[t]; })
      .filter(function (p) { return p.items.some(function (it) { return it.role === 'item'; }); });
  }

  function getPacks() {
    return fetch(PACKS_SECTION_URL, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // the section wraps its JSON in <script type="application/json"
        // data-api-content> — parse it out of the section-rendering envelope
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('script[data-api-content]');
        if (!node) throw new Error('api-cart-packs: no data-api-content');
        var data = JSON.parse(node.textContent);
        if (data && Array.isArray(data.packs)) return resolvePackUnits(data.packs);
        throw new Error('api-cart-packs: unexpected payload');
      })
      .catch(function () {
        // section unreachable — degrade to /cart.js grouping (unmerged packs only)
        return fetchCart().then(packsFromCart);
      });
  }

  /* ------------------------------------------- C4: teardown / replace / remove */

  // Change every line of a pack to qty 0. keepJournal: removePack keeps the
  // journal in the cart (it is a real product the shopper chose; only its pack
  // discount lapses) while replacePack tears it down because the new items
  // array re-adds it (the builder always includes the journal line, and the
  // drawer rebuilds payloads from pack.items which include it too).
  function teardown(token, keepJournal) {
    return fetchCart().then(function (cart) {
      var lines = (cart.items || []).filter(function (l) {
        var props = l.properties || {};
        if (props[PACK_PROP] !== token) return false;
        if (keepJournal && props[ROLE_PROP] === 'journal') return false;
        return true;
      });
      var keys = lines.map(function (l) { return l.key; });
      // #119: snapshot the EXACT /cart/add.js payload for the lines we're
      // about to zero out, BEFORE any change.js call goes out. teardown is
      // the only place that still has the real cart lines (variant id,
      // quantity, properties) in hand — by the time a later add fails,
      // /cart.js no longer shows them. replacePack uses this to restore the
      // pack if the re-add fails.
      var removedItems = lines.map(function (l) {
        return { id: l.variant_id, quantity: l.quantity, properties: l.properties || {} };
      });
      var chain = Promise.resolve(cart);
      keys.forEach(function (key) {
        // sequential change-by-key: keys of untouched lines stay stable
        chain = chain.then(function () {
          return postJSON(LT_ROOT + 'cart/change.js', { id: key, quantity: 0 });
        });
      });
      return chain.then(function () { return { removedItems: removedItems }; });
    });
  }

  function replacePack(token, items) {
    return enqueue(function () {
      // #119 (th-build-a-pack-1): teardown used to run unconditionally before
      // the re-add, with nothing restoring the old lines if the add failed
      // (422 on a variant that sold out since the pack was built, a network
      // drop, etc) — a routine quantity tap could empty the customer's whole
      // pack. removedItems is teardown's own snapshot of what it just
      // removed; on an add failure we re-add exactly that payload before
      // surfacing the error, so the cart is restored to its pre-tap state.
      var removedItems = null;
      return teardown(token, false)
        .then(function (result) {
          removedItems = result && result.removedItems;
          if (!items || !items.length) return null;
          return postJSON(LT_ROOT + 'cart/add.js', { items: items });
        })
        .catch(function (addErr) {
          // removedItems is only populated once teardown has FULLY resolved.
          // A failure DURING teardown (some lines already zeroed, the rest
          // untouched, add never attempted) leaves removedItems null — that
          // partial-teardown case is a known, undocumented-fix gap (see
          // #119 report); we do not guess at a restore for it here.
          if (!removedItems || !removedItems.length) throw addErr;
          return postJSON(LT_ROOT + 'cart/add.js', { items: removedItems }).then(
            function () {
              // Restore succeeded — the customer's pack is back exactly as it
              // was before the tap. Still surface the ORIGINAL add error so
              // the caller shows its existing "couldn't update" message
              // (money-display rule: callers re-fetch /cart.js for real
              // totals on their own catch path; nothing is computed here).
              throw addErr;
            },
            function (restoreErr) {
              // Restore ALSO failed: the pack may now be genuinely gone.
              // Surface a distinct, clearly-labeled error so this doesn't
              // read as an ordinary "please retry" failure in the console.
              console.error('replacePack: add failed AND restore failed — pack may be lost from the cart', {
                token: token, addError: addErr, restoreError: restoreErr
              });
              var combined = new Error('replacePack: add failed and restore failed for token ' + token);
              combined.addError = addErr;
              combined.restoreError = restoreErr;
              throw combined;
            }
          );
        })
        .then(fetchCart)
        .then(dispatchCartChange);
    });
  }

  function removePack(token) {
    return enqueue(function () {
      return teardown(token, true).then(fetchCart).then(dispatchCartChange);
    });
  }

  // internal (not in the C4 surface): plain line ops share the same queue so
  // a regular-line change can never interleave with a pack teardown/re-add
  function changeLine(key, quantity) {
    return enqueue(function () {
      return postJSON(LT_ROOT + 'cart/change.js', { id: key, quantity: quantity })
        .then(fetchCart)
        .then(dispatchCartChange);
    });
  }

  function addLine(variantId, quantity) {
    return enqueue(function () {
      return postJSON(LT_ROOT + 'cart/add.js', { items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }] })
        .then(fetchCart)
        .then(dispatchCartChange);
    });
  }

  window.LtPackCart = { getPacks: getPacks, replacePack: replacePack, removePack: removePack, __ltRuntime: 'drawer-2026-07-18' };

  /* ============================================================
     PART 1.5 — units-aware header cart badge (Nick, 2026-07-18)

     The Liquid-rendered header badge shows cart.item_count, where a merged
     5-item pack is ONE line — customers add 5 things and the badge says
     "(1)". The real count is Σ pack units + every other line's quantity,
     computable from /cart.js alone: the merged parent carries _lt_units as
     a line property (live-proven; the transform writes it), and everything
     else (regular lines, loose/unmerged pack items, journal, mystery)
     counts by plain quantity. This matches the drawer's displayCount().

     theme.js (Totals) rewrites the same [data-header-cart-count] nodes with
     item_count on every theme:cart:change, in a listener-registration order
     we don't control — so ordering games are not enough. A MutationObserver
     on each badge node re-asserts the corrected count after ANY foreign
     write; our own write makes the text equal the target, so the observer
     settles without looping.
     ============================================================ */

  var UNITS_PROP = '_lt_units';
  var BADGE_ATTR = 'data-header-cart-count';
  var badgeCount = null; // last known real count; null until a cart snapshot

  function badgeCountFromCart(cart) {
    return (cart.items || []).reduce(function (s, line) {
      var props = line.properties || {};
      if (line.has_components || String(line.variant_id) === PARENT_VARIANT_ID) {
        var u = parseInt(props[UNITS_PROP], 10);
        return s + (isNaN(u) ? line.quantity : u); // no _lt_units: degrade to line qty
      }
      return s + line.quantity;
    }, 0);
  }

  var badgeObserver = typeof MutationObserver === 'function'
    ? new MutationObserver(function () { paintBadge(); })
    : null;

  function paintBadge() {
    if (badgeCount === null) return;
    var want = '(' + badgeCount + ')';
    document.querySelectorAll('[' + BADGE_ATTR + ']').forEach(function (node) {
      if (badgeObserver && !node.__ltBadgeObserved) {
        node.__ltBadgeObserved = true;
        badgeObserver.observe(node, { childList: true, characterData: true, subtree: true });
      }
      if (node.getAttribute(BADGE_ATTR) !== String(badgeCount)) node.setAttribute(BADGE_ATTR, badgeCount);
      if (node.textContent.replace(/\s+/g, '') !== want) node.textContent = want;
    });
  }

  function setBadge(count) {
    badgeCount = count;
    paintBadge();
  }

  // recount on every cart change, ours or the theme's; a detail without a
  // cart payload triggers a fetch so the count stays authoritative
  document.addEventListener('theme:cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && Array.isArray(cart.items)) setBadge(badgeCountFromCart(cart));
    else fetchCart().then(function (c) { setBadge(badgeCountFromCart(c)); }).catch(function () {});
  });

  // initial page load: the Liquid badge starts as item_count — correct it as
  // soon as we have cart data (runs on every page with a header badge, even
  // where the drawer itself is absent)
  function initBadge() {
    var node = document.querySelector('[' + BADGE_ATTR + ']');
    if (!node) return;
    // #69 P2-6: /cart.js is a fresh, uncacheable request fired on EVERY page
    // view just to correct this badge — but header.liquid already renders
    // item_count straight into this same attribute server-side. When that
    // starting value is 0 there is nothing a merged pack could be hiding
    // (a pack parent line would make item_count >= 1), so the fetch is
    // provably a no-op and can be skipped outright.
    if (parseInt(node.getAttribute(BADGE_ATTR), 10) === 0) return;
    fetchCart().then(function (c) { setBadge(badgeCountFromCart(c)); }).catch(function () {});
  }

  /* ============================================================
     PART 2 — drawer runtime
     ============================================================ */

  // Cart-icon delegation binds IMMEDIATELY at script evaluation, in the
  // CAPTURE phase (Vera: the first icon click within seconds of page load was
  // intermittently swallowed — a bubble-phase listener bound after init loses
  // to any stock handler that stops propagation, and to the init/DCL window).
  // Capture on document fires before anything else can eat the event; if the
  // runtime hasn't booted yet the intent is remembered and honored at boot.
  var drawerApi = null;          // set by initDrawer
  var pendingDrawerOpen = false; // click arrived before the runtime booted

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('[data-drawer-toggle="drawer-cart"]');
    if (!toggle) return;
    if (!document.querySelector('[data-lt-drawer]')) return; // no drawer here (/cart): let the link navigate
    e.preventDefault();
    if (drawerApi) drawerApi.toggle();
    else pendingDrawerOpen = true;
  }, true);

  function initDrawer() {
    var root = document.querySelector('[data-lt-drawer]');
    if (!root) return; // page without the drawer (e.g. /cart, builder-only harness)

    var cfg = {
      geo: root.dataset.geo || 'OTHER',
      threshold: parseInt(root.dataset.shipThreshold, 10) || 0,
      t1: parseInt(root.dataset.t1, 10) || 5,
      t1p: parseInt(root.dataset.t1p, 10) || 10,
      t2: parseInt(root.dataset.t2, 10) || 10,
      t2p: parseInt(root.dataset.t2p, 10) || 15,
      // #116 round 2: gift-with-purchase thumbnail. cfg.t2 above is
      // ALSO the free-gift threshold -- no second "10" anywhere in this
      // file, no new setting: the drawer already had this exact number for
      // the tier-2 discount and the gift rides the same line.
      // #192 round 3: the freebie set's ONE photo. cart-drawer.liquid sets
      // data-gift-asset from its own "Freebie set photo" picker (a global section
      // cannot read the builder section's settings); empty = giftThumbHtml() below
      // draws no thumb box at all.
      giftAsset: root.dataset.giftAsset || '',
      mysteryVariant: (root.dataset.mysteryVariant || '').trim(),
      builderUrl: root.dataset.builderUrl || '/pages/build-a-pack',
      cartUrl: root.dataset.cartUrl || '/cart',
      moneyFormat: (root.dataset.moneyFormat || '').replace(/<[^>]*>/g, '') || '€{{amount}}'
    };

    var el = {
      panel: root.querySelector('.lt-cd__panel'),
      scrim: root.querySelector('[data-lt-cd-scrim]'),
      close: root.querySelector('[data-lt-cd-close]'),
      count: root.querySelector('[data-lt-cd-count]'),
      ship: root.querySelector('[data-lt-cd-ship]'),
      shipBelow: root.querySelector('[data-lt-cd-ship-below]'),
      shipReached: root.querySelector('[data-lt-cd-ship-reached]'),
      shipAway: root.querySelector('[data-lt-cd-ship-away]'),
      shipFill: root.querySelector('[data-lt-cd-ship-fill]'),
      items: root.querySelector('[data-lt-cd-items]'),
      empty: root.querySelector('[data-lt-cd-empty]'),
      mystery: root.querySelector('[data-lt-cd-mystery]'),
      mysteryOff: root.querySelector('[data-lt-cd-mystery-off]'),
      mysteryOn: root.querySelector('[data-lt-cd-mystery-on]'),
      footer: root.querySelector('[data-lt-cd-footer]'),
      subtotal: root.querySelector('[data-lt-cd-subtotal]'),
      shipnote: root.querySelector('[data-lt-cd-shipnote]'),
      checkoutTotal: root.querySelector('[data-lt-cd-checkout-total]'),
      error: root.querySelector('[data-lt-cd-error]'),
      edit: root.querySelector('[data-lt-cd-edit]'),
      editScrim: root.querySelector('[data-lt-cd-edit-scrim]'),
      editThumb: root.querySelector('[data-lt-cd-edit-thumb]'),
      editName: root.querySelector('[data-lt-cd-edit-name]'),
      editSub: root.querySelector('[data-lt-cd-edit-sub]'),
      editQty: root.querySelector('[data-lt-cd-edit-qty]'),
      editDec: root.querySelector('[data-lt-cd-edit-dec]'),
      editInc: root.querySelector('[data-lt-cd-edit-inc]'),
      editOptions: root.querySelector('[data-lt-cd-edit-options]'),
      editDone: root.querySelector('[data-lt-cd-edit-done]'),
      editSheet: root.querySelector('.lt-cd__editsheet'),
      editClose: root.querySelector('[data-lt-cd-edit-close]'),
      status: root.querySelector('[data-lt-cd-status]') // #68 finding 16: small composed live region
    };

    // #68 finding 16: one small announcement at a time ("Quantity of X now
    // 3", "X removed from your pack") instead of the old list-wide aria-live
    // that re-read the entire cart on every render.
    function announce(msg) {
      if (el.status) el.status.textContent = msg;
    }

    var state = {
      cart: null,
      packs: [],
      open: false,
      stale: true,
      loading: false,
      syncTokens: {},   // token -> true while a replacePack round-trip runs
      flushTimers: {},  // token -> debounce timer for rapid qty taps
      flushGen: {},     // token -> generation; bumped per edit so a stale
                        // in-flight round-trip never clobbers newer taps
      sheet: null,      // { token, variantId, qty, selected: {optName: value} }
      justAdded: null,  // { key, variantId } from an add-bearing theme:cart:change
                        // (#18 r3e option A): that line renders pinned above the
                        // pack block for instant add-feedback. Never derived from
                        // timestamps; set only by the add event payload, cleared
                        // when the drawer closes — a reopened drawer regroups.
      lastFocus: null,
      sheetLastFocus: null // element to restore focus to when the sheet closes
    };

    var TYPE_LABEL = {
      'stickers': 'Sticker sheet', 'sticker': 'Sticker sheet', 'sticker sheet': 'Sticker sheet',
      'sticker sheets': 'Sticker sheet', 'washi': 'Washi tape', 'washi tape': 'Washi tape',
      'cards': 'Postcard', 'card': 'Postcard', 'postcard': 'Postcard', 'postcards': 'Postcard'
    };
    // #142 round 2: product-type labels for the types TYPE_LABEL above
    // doesn't cover (Greeting Card and the other 8 non-sticker/washi/card
    // types), read once from the data-lt-type-labels script tag
    // (cart-drawer.liquid, same bridge as section-lt-build-a-pack.liquid).
    // Absent node / bad JSON -> stays null, typeLabel() below falls back to
    // raw, i.e. today's behaviour.
    var ltTypeLabels = null;
    // #143: same bridge, extended with a flat "strings" map for the error/
    // status literals this file writes at runtime. LT() is the single lookup
    // point; absent/blank map -> the literal fallback (today's behaviour) —
    // same contract as ltTypeLabels above. On the bare /cart page this tag
    // doesn't render (it's inside cart-drawer.liquid's `template.name !=
    // 'cart'` guard, pre-#143 #142 decision, unchanged here), so these
    // messages fall back to English there; flagged in the report.
    var ltStrings = {};
    (function parseTypeLabels() {
      // #143 round 3: on the builder page the builder renders a SECOND
      // [data-lt-type-labels] tag with a different string map; ask for the
      // drawer's own tag by owner so the two never read each other's map.
      var node = document.querySelector('[data-lt-type-labels][data-lt-bridge="drawer"]') ||
        document.querySelector('[data-lt-type-labels]');
      if (!node) return;
      var parsed;
      try { parsed = JSON.parse(node.textContent); } catch (e) { return; }
      if (parsed && parsed.types) ltTypeLabels = parsed.types;
      if (parsed && parsed.strings) ltStrings = parsed.strings;
    })();
    function LT(key, fallback) {
      var v = ltStrings[key];
      if (!v) return fallback;
      // Vera B2 (2026-09-26): Shopify's `t` filter HTML-escapes every non-_html locale
      // value before `json` wraps it, so "you've" arrived as "you&#39;ve" and textContent
      // showed the entity literally. Decode once here; _html keys are real HTML already.
      if (typeof v !== 'string' || v.indexOf('&') === -1 || /_html$/.test(key)) return v;
      var ta = document.createElement('textarea');
      ta.innerHTML = v;
      return ta.value;
    }
    var TRASH_SVG = '<svg width="12" height="13" viewBox="0 0 12 13" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" aria-hidden="true"><path d="M1 3h10M4 3V1.5h4V3M2.3 3l.6 8.5h6.2L9.7 3M4.8 5.3v4M7.2 5.3v4"></path></svg>';

    /* ------------------------------------------------------------ helpers */

    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    // minimal Shopify money-format renderer (shop.money_format placeholders)
    function formatMoney(cents) {
      cents = Math.round(cents || 0);
      var fmt = cfg.moneyFormat;
      function grouped(n, decimals, thousands, decimal) {
        var neg = n < 0 ? '-' : '';
        var fixed = Math.abs(n / 100).toFixed(decimals);
        var parts = fixed.split('.');
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return neg + whole + (parts[1] ? decimal + parts[1] : '');
      }
      var value;
      if (fmt.indexOf('{{amount_no_decimals_with_comma_separator}}') !== -1) {
        value = grouped(cents, 0, '.', ',');
        return fmt.replace('{{amount_no_decimals_with_comma_separator}}', value);
      }
      if (fmt.indexOf('{{amount_with_comma_separator}}') !== -1) {
        value = grouped(cents, 2, '.', ',');
        return fmt.replace('{{amount_with_comma_separator}}', value);
      }
      if (fmt.indexOf('{{amount_no_decimals}}') !== -1) {
        value = grouped(cents, 0, ',', '.');
        return fmt.replace('{{amount_no_decimals}}', value);
      }
      value = grouped(cents, 2, ',', '.');
      return fmt.replace('{{amount}}', value);
    }

    // #142 (2026-09-21, Ian): TYPE_LABEL's curated names win when present
    // (unchanged); everything else — Greeting Card and the other types
    // TYPE_LABEL never covered — now goes through the locale map instead of
    // falling straight to the raw Shopify value, so Dutch pages pick up a
    // translation once nl.json exists.
    function typeLabel(raw) {
      if (!raw) return '';
      var lower = String(raw).toLowerCase();
      // #143 round 3: TYPE_LABEL's English values used to win outright, so a
      // Dutch drawer still said "Sticker sheet". Its keys stay logic (lower-
      // cased raw types); only the output word goes through LT().
      var en = TYPE_LABEL[lower];
      if (en === 'Sticker sheet') return LT('type_sticker_sheet', en);
      if (en === 'Washi tape') return LT('type_washi_tape', en);
      if (en === 'Postcard') return LT('type_postcard', en);
      return (ltTypeLabels && ltTypeLabels[raw]) || raw;
    }

    // #69 P3-13: appends a Shopify CDN width= param, same guard as before
    // (leaves an already-sized URL alone rather than doubling the param).
    function withWidth(src, w) {
      if (src.indexOf('width=') !== -1) return src;
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }

    function imgTag(src, alt, width) {
      if (!src) return '';
      var w = width || 128;
      var url1x = withWidth(src, w);
      var url2x = withWidth(src, w * 2);
      // #69 P3-13: width/height attrs give the browser the (square, in every
      // one of these thumbs) aspect ratio before the image loads, so the row
      // doesn't jump as thumbs pop in; the 1x/2x srcset — new, was a single
      // fetch before — gives retina screens a sharper thumb instead of the
      // same asset stretched. Every caller's box is square (CSS forces
      // width/height:100% on the img regardless), so one dimension covers
      // both attributes.
      return '<img src="' + escapeHtml(url1x) + '" srcset="' + escapeHtml(url1x) + ' 1x, ' + escapeHtml(url2x) + ' 2x" alt="' + escapeHtml(alt || '') + '" width="' + w + '" height="' + w + '" loading="lazy">';
    }

    function isMobile() {
      return window.matchMedia('(max-width: 640px)').matches;
    }

    function packItems(pack) {
      return (pack.items || []).filter(function (it) { return it.role !== 'journal'; });
    }

    // #62: the journal rides along in pack.items (both getPacks paths push it)
    // so its edit-mode preload keeps working; the drawer nests it as a
    // sub-row on the card instead of excluding it entirely.
    function packJournal(pack) {
      return (pack.items || []).filter(function (it) { return it.role === 'journal'; })[0] || null;
    }

    // real /cart.js line for a pack's journal (pack.items entries have no
    // line `key` — lt-pack-item-json.liquid never emits one — so the remove
    // control looks the key up fresh from state.cart at click time).
    function journalLineForToken(token) {
      if (!state.cart) return null;
      return (state.cart.items || []).filter(function (line) {
        var props = line.properties || {};
        return props[PACK_PROP] === token && props[ROLE_PROP] === 'journal';
      })[0] || null;
    }

    // tier math counts ITEMS (units x qty); merged packs carry the transform's
    // authoritative _lt_units, recomputed locally during optimistic updates
    function packUnits(pack) {
      return packItems(pack).reduce(function (s, it) {
        return s + (it.units || 1) * (it.quantity || 1);
      }, 0);
    }

    // #121 (th-build-a-pack-2/7, audit 2026-08-24): the true UNDISCOUNTED
    // per-unit price. it.price is Shopify's final_price -- on a merged pack
    // that already carries the per-line penny allocation of the pack-tier
    // discount, so using it here double-discounts during the optimistic
    // (syncing) window: a 5x€2.79 pack at 10% off (true total €12.56) briefly
    // showed ~€11.29 -- about 10% under what checkout actually charges (see
    // the node test's Fixture A for the exact cents). The undiscounted
    // variant list price already rides along on every item in it.variants
    // (lt-pack-item-json.liquid emits "price":{{ v.price }} -- the variant's
    // own price, never final_price) -- matched by variantId so an
    // edited-variant item (applyEditSheet above swaps it.variantId on a
    // material/design change) still resolves correctly. Falls back to
    // it.price only if no variant entry matches (should not happen -- every
    // product emits at least one variant in prod.variants -- kept as a safety
    // net, not a silent guess).
    function unitOriginalCents(it) {
      var variants = it.variants || [];
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(it.variantId)) return variants[i].price;
      }
      return it.price || 0;
    }

    function packOriginalCents(pack) {
      return packItems(pack).reduce(function (s, it) {
        return s + unitOriginalCents(it) * (it.quantity || 1);
      }, 0);
    }

    // #121: pure pack-foot money math, pulled out of packFootParts (below) so
    // it is node-importable without dragging in the DOM --
    // theme-8.1.1-migrated/tests/pack-cart-money.logic.test.mjs loads this
    // exact function out of the shipped file. `items` is the pack's non-
    // journal items (packItems(pack) -- the journal's own price is added back
    // by the caller); `pct` is the already-resolved tier percentage
    // (tierPct(units)); `pack` supplies the server-authoritative fields used
    // ONLY on the non-syncing branch (untouched by this ticket -- see
    // packFootParts' comment on why that branch must stay as-is). Floors the
    // discount cents, matching engineNet() in lt-build-a-pack.js and the
    // checkout engine -- never Math.round (the repo's own documented
    // round-vs-floor lesson).
    function computePackFoot(params) {
      var items = params.items || [];
      var pct = params.pct || 0;
      var syncing = !!params.syncing;
      var pack = params.pack || {};

      var itemsOriginal = items.reduce(function (s, it) {
        return s + unitOriginalCents(it) * (it.quantity || 1);
      }, 0);

      var original = syncing ? itemsOriginal : (pack.packOriginal || itemsOriginal);
      var discountAmount, discountPct;
      if (syncing) {
        discountAmount = Math.floor(original * pct / 100);
        discountPct = pct;
      } else if (pack.discount && pack.discount.amount > 0) {
        discountAmount = pack.discount.amount;
        discountPct = pct || Math.round(pack.discount.amount / original * 100);
      } else {
        discountAmount = 0;
        discountPct = 0;
      }
      var subtotal = syncing ? original - discountAmount : (pack.packTotal || original - discountAmount);

      return { original: original, discountAmount: discountAmount, discountPct: discountPct, subtotal: subtotal };
    }

    function tierPct(units) {
      if (units >= cfg.t2) return cfg.t2p;
      if (units >= cfg.t1) return cfg.t1p;
      return 0;
    }

    function tierNudge(units) {
      if (units >= cfg.t1 - 2 && units < cfg.t1) return { need: cfg.t1 - units, pct: cfg.t1p };
      if (units >= cfg.t2 - 2 && units < cfg.t2) return { need: cfg.t2 - units, pct: cfg.t2p };
      return null;
    }

    function findPack(token) {
      return state.packs.filter(function (p) { return p.token === token; })[0] || null;
    }

    function findItem(pack, variantId) {
      return (pack.items || []).filter(function (it) {
        return String(it.variantId) === String(variantId) && it.role !== 'journal';
      })[0] || null;
    }

    // cart lines that render as plain rows: not a merged parent, not a loose
    // pack item (those live in the pack card), not the mystery line (pinned
    // row). The journal (role journal) is a plain row ONLY when its pack has
    // no rendered card for that token (#62: unmerged 1-item pack + journal
    // still gets a card — see getPacks' role:'item'-only filter — so this is
    // "no card", not "no merge"; a journal whose pack was removed also lands
    // here). When a card IS rendered it nests the journal instead, via
    // findPack — the exact same lookup packCardHtml itself uses.
    function regularLines() {
      if (!state.cart) return [];
      return (state.cart.items || []).filter(function (line) {
        var props = line.properties || {};
        if (line.has_components) return false;                 // merged parent
        if (props[PACK_PROP] && props[ROLE_PROP] !== 'journal') return false; // loose pack item
        if (props[ROLE_PROP] === 'journal' && props[PACK_PROP] && findPack(props[PACK_PROP])) return false; // nested in its pack card
        if (cfg.mysteryVariant && String(line.variant_id) === cfg.mysteryVariant) return false;
        return true;
      });
    }

    function mysteryLine() {
      if (!cfg.mysteryVariant || !state.cart) return null;
      return (state.cart.items || []).filter(function (line) {
        return String(line.variant_id) === cfg.mysteryVariant;
      })[0] || null;
    }

    // display count = pack items + each rendered pack's nested journal qty
    // (#62: no longer in regularLines when its card renders) + plain line
    // quantities (incl. an orphan journal + mystery). Every journal is
    // counted exactly once either way — never both, never neither.
    function displayCount() {
      var n = state.packs.reduce(function (s, p) {
        var j = packJournal(p);
        return s + packUnits(p) + (j ? (j.quantity || 1) : 0);
      }, 0);
      n += regularLines().reduce(function (s, l) { return s + l.quantity; }, 0);
      var m = mysteryLine();
      if (m) n += m.quantity;
      return n;
    }

    /* ---------------------------------------------------------- rendering */

    function packRowHtml(pack, it) {
      var qty = it.quantity || 1;
      var sub = typeLabel(it.type);
      if (it.material) sub += (sub ? ' · ' : '') + it.material;
      // #69 P2-5: the chip is now ALWAYS in the markup (hidden via the
      // `hidden` attribute at qty<=1) instead of being conditionally
      // inserted/omitted. patchRowQty() below only ever toggles `hidden` +
      // textContent on an existing node — never inserts/removes it — which
      // is what lets a qty tap update the row in place.
      var chip = '<span class="lt-cd__qtychip" data-lt-row-chip' + (qty > 1 ? '' : ' hidden') + '>×' + qty + '</span>';
      // #68 finding 21: every one of these controls repeats verbatim across
      // every row in the pack ("Fewer", "More", "Remove", "Edit") — a screen
      // reader's buttons-list reads N identical entries with no way to tell
      // rows apart. Every label is composed with the item's own name.
      var nameAttr = escapeHtml(it.name);
      return (
        '<div class="lt-cd__rowwrap" data-lt-rowwrap data-token="' + escapeHtml(pack.token) + '" data-vid="' + escapeHtml(String(it.variantId)) + '">' +
          '<div class="lt-cd__rowactions-mob">' +
            '<button type="button" class="lt-cd__mobact lt-cd__mobact--edit" data-lt-row-edit aria-label="' + LT('edit_of_full', 'Edit {name}').replace('{name}', nameAttr) + '">' + LT('edit_bare', 'Edit') + '</button>' +
            '<button type="button" class="lt-cd__mobact lt-cd__mobact--remove" data-lt-row-remove aria-label="' + LT('remove_of_pack_full', 'Remove {name} from pack').replace('{name}', nameAttr) + '">' + LT('remove_bare', 'Remove') + '</button>' +
          '</div>' +
          '<div class="lt-cd__row" data-lt-row>' +
            '<div class="lt-cd__rowthumb">' + imgTag(it.image, it.name, 68) + '</div>' +
            '<div class="lt-cd__rowmain">' +
              '<div class="lt-cd__rowname">' + escapeHtml(it.name) + chip + '</div>' +
              (sub ? '<div class="lt-cd__rowsub">' + escapeHtml(sub) + '</div>' : '') +
            '</div>' +
            // #53 (Nick 2026-07-21): show the UNDISCOUNTED catalog line price.
            // it.price is final_price, which carries Shopify's per-line penny
            // allocation of the pack discount - five identical €2.79 sheets
            // rendered as €2.52/€2.51/€2.51... and looked broken. The discount
            // is communicated once by the pack-discount line below; rows show
            // what each item costs.
            // #121 fix-round (Vera Finding A, 2026-08-25): price from
            // unitOriginalCents(it) * qty - the SAME source patchRowQty uses -
            // not it.originalLinePrice. On the mobile Edit-sheet path the qty
            // changes via applyEditSheet -> render() -> packRowHtml (the inline
            // stepper + patchRowQty do not run under 640px), and
            // it.originalLinePrice is still the server value for the OLD qty
            // there, so the row froze at the pre-tap price for ~2s. qty here is
            // the live optimistic quantity; unitOriginalCents equals the server
            // originalLinePrice/qty on a settled render, so no regression.
            '<span class="lt-cd__rowprice" data-lt-row-price>' + formatMoney(unitOriginalCents(it) * qty) + '</span>' +
            '<span class="lt-cd__rowchev" aria-hidden="true">‹</span>' +
            '<div class="lt-cd__rowactions">' +
              '<div class="lt-cd__microqty">' +
                '<button type="button" data-lt-row-dec aria-label="' + LT('fewer_of_full', 'Fewer {name}').replace('{name}', nameAttr) + '"' + (qty <= 1 ? ' disabled' : '') + '>−</button>' +
                '<span data-lt-row-qtytext>' + qty + '</span>' +
                '<button type="button" data-lt-row-inc aria-label="' + LT('more_of_full', 'More {name}').replace('{name}', nameAttr) + '">+</button>' +
              '</div>' +
              // opens the SAME edit sheet as mobile, as a desktop dialog — no
              // navigation (Nick, 2026-07-18; only the pack-card header "Edit
              // pack" link still goes to the builder's edit mode)
              '<button type="button" class="lt-cd__rowedit" data-lt-row-edit aria-label="' + LT('edit_of_full', 'Edit {name}').replace('{name}', nameAttr) + '" title="' + LT('change_qty_title', 'Change quantity, design or finish') + '">' + LT('edit_bare', 'Edit') + '</button>' +
              '<button type="button" class="lt-cd__rowtrash" data-lt-row-remove aria-label="' + LT('remove_of_pack_full', 'Remove {name} from pack').replace('{name}', nameAttr) + '" title="' + LT('remove_from_pack', 'Remove from pack') + '">' + TRASH_SVG + '</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    // #62: journal sub-row — divider + quiet label, thumb, name (+ qty chip
    // if ever >1), price. Price follows the same del/final pattern as
    // regularLineHtml, comparing the journal LINE's original vs final price
    // (its own discount, independent of the pack's own tier discount below).
    // The remove control carries only data-token; removeJournal looks the
    // real cart line (and its key) up fresh from state.cart on click.
    // shared with packCardHtml's Pack total math (#63) — one source for what
    // the journal actually costs on this line, so the card-bottom total can
    // never drift from what the row itself displays
    function journalLinePriceCents(it) {
      var qty = it.quantity || 1;
      return it.linePrice || (it.price || 0) * qty;
    }

    function packJournalRowHtml(pack, it) {
      var qty = it.quantity || 1;
      var linePrice = journalLinePriceCents(it);
      var discounted = (it.originalLinePrice || 0) > linePrice;
      var priceHtml = (discounted ? '<del>' + formatMoney(it.originalLinePrice) + '</del>' : '') + formatMoney(linePrice);
      var chip = qty > 1 ? '<span class="lt-cd__qtychip">×' + qty + '</span>' : '';
      return (
        '<div class="lt-cd__journalwrap" data-lt-journal-row data-token="' + escapeHtml(pack.token) + '">' +
          '<div class="lt-cd__journaldivider"><span>' + LT('added_with_this_pack', 'Added with this pack') + '</span></div>' +
          '<div class="lt-cd__journalrow">' +
            '<div class="lt-cd__journalthumb">' + imgTag(it.image, it.name, 60) + '</div>' +
            '<div class="lt-cd__journalmain">' +
              '<div class="lt-cd__journalname">' + escapeHtml(it.name) + chip + '</div>' +
            '</div>' +
            '<span class="lt-cd__journalprice">' + priceHtml + '</span>' +
            // #68 finding 21 (same fix pattern as packRowHtml above): title-only trash button
            '<button type="button" class="lt-cd__journaltrash" data-lt-journal-remove aria-label="' + LT('remove_of_full', 'Remove {name}').replace('{name}', escapeHtml(it.name)) + '" title="' + LT('remove_this_item_title', 'Remove this item') + '">' + TRASH_SVG + '</button>' +
          '</div>' +
        '</div>'
      );
    }

    // #116 round 2: gift-with-purchase thumbnail. A static asset URL (not a Shopify
    // product image), so imgTag()'s withWidth() CDN transform param doesn't apply:
    // a density-only (1x/2x, same bytes) srcset instead of a fake width param.
    // #192 round 3: the one set photo (cfg.giftAsset above). Without an asset URL the
    // row has NO thumb box at all (an empty hatch square would read as a missing image).
    function giftThumbHtml() {
      if (!cfg.giftAsset) return '';
      var url = cfg.giftAsset;
      return '<div class="lt-cd__giftthumb"><img src="' + escapeHtml(url) + '" srcset="' + escapeHtml(url) + ' 1x, ' + escapeHtml(url) + ' 2x" alt="" width="30" height="30" loading="lazy"></div>';
    }

    // #116 round 2: unconditional gift-with-purchase sub-row, nested inside
    // a QUALIFYING pack's own card -- same visual family as
    // packJournalRowHtml above (divider + quiet label + thumb + name +
    // price-slot) but never removable/editable: no button at all, because
    // this reward is automatic and the brief is explicit that it is
    // "puur presentatie: geen cart-mutatie, geen product". One row PER
    // qualifying pack (own decision, documented in the round-2 report,
    // matrix row 8): each full pack earns its own freebie set, mirroring how
    // the journal sub-row already nests inside the ONE card it rode in
    // with, never a single line detached from any specific pack.
    function packGiftRowHtml() {
      return (
        '<div class="lt-cd__giftwrap" data-lt-pack-gift>' +
          '<div class="lt-cd__giftdivider"><span>' + LT('free_gift_label', 'Free gift') + '</span></div>' +
          '<div class="lt-cd__giftrow">' +
            giftThumbHtml() +
            '<div class="lt-cd__giftmain"><div class="lt-cd__giftname">' + LT('freebie_set_alt', 'Four exclusive winter freebies') + '</div></div>' +
            '<span class="lt-cd__giftprice">' + LT('free_label', 'Free') + '</span>' +
          '</div>' +
        '</div>'
      );
    }

    // #69 P2-5: extracted out of packCardHtml so the qty-tap patch path
    // (patchPackTotals below) can recompute count/discount/subtotal/total/
    // nudge from the exact same code the full card build uses — one source
    // of truth for this money math, no second copy that could drift.
    function packFootParts(pack, editHref) {
      var units = packUnits(pack);
      // #116 round 2: units >= cfg.t2 is the SAME threshold check the tier
      // discount two lines below already makes via tierPct(units) -- no
      // separate/parallel gift-unlock computation.
      var giftHtml = units >= cfg.t2 ? packGiftRowHtml() : '';
      var journal = packJournal(pack);
      var syncing = !!state.syncTokens[pack.token];

      // authoritative money from the server render; recomputed locally only
      // while an optimistic mutation is in flight. This math is STATIONERY
      // TIER ONLY — the journal's own discount (if any) lives entirely on its
      // own row (packJournalRowHtml's del/mark price) and is never folded in
      // here, so it is never double-counted or re-derived from a percentage.
      // #121: money computation itself now lives in computePackFoot (pure,
      // node-tested) -- this call is the ONLY thing that changed in this
      // function; the non-syncing (server-authoritative) branch's values are
      // byte-identical to before.
      var pct = tierPct(units);
      var money = computePackFoot({ items: packItems(pack), pct: pct, syncing: syncing, pack: pack });
      var discountAmount = money.discountAmount;
      var discountPct = money.discountPct;
      // "subtotal" = stationery net after the pack-tier discount — this is
      // the exact number that used to be labeled "Pack total" before #63.
      // With no journal nested it still IS the pack total (see the no-journal
      // branch below); with a journal nested the real bottom-line total has
      // to add the journal's real line price too (#63: Nick's screenshot —
      // a card showing €65.08 of goods can't claim a €20.09 total).
      var subtotal = money.subtotal;
      var journalLinePrice = journal ? journalLinePriceCents(journal) : 0;
      var cardTotal = subtotal + journalLinePrice;

      var nudge = tierNudge(units);
      var discRow = discountAmount > 0
        ? '<div class="lt-cd__packdisc"><span>' + LT('pack_discount_pct', 'Pack discount ({pct}%)').replace('{pct}', discountPct) + '</span><span>−' + formatMoney(discountAmount) + '</span></div>'
        : '';

      // #63: a nested journal splits the footer into two sections around the
      // journal row — discount + a labeled Subtotal BEFORE it (so the
      // journal's own inline price stays its only discount display), Pack
      // total (now covering both) AFTER it. No journal: byte-identical to
      // the pre-#63 layout (discount + Pack total only, no Subtotal line) —
      // Subtotal and Pack total must never show the same amount.
      var footHtml;
      if (journal) {
        footHtml =
          '<div class="lt-cd__packfoot">' +
            discRow +
            '<div class="lt-cd__packsubtotalrow"><span>' + LT('subtotal', 'Subtotal') + '</span><span>' + formatMoney(subtotal) + '</span></div>' +
          '</div>' +
          packJournalRowHtml(pack, journal) +
          '<div class="lt-cd__packfoot">' +
            '<div class="lt-cd__packtotalrow"><span>' + LT('pack_total_label', 'Pack total') + '</span><span>' + formatMoney(cardTotal) + '</span></div>' +
            giftHtml +
            '<button type="button" class="lt-cd__quietlink" data-lt-remove-pack>' + LT('remove_pack_button', 'Remove pack') + '</button>' +
          '</div>';
      } else {
        footHtml =
          '<div class="lt-cd__packfoot">' +
            discRow +
            '<div class="lt-cd__packtotalrow"><span>' + LT('pack_total_label', 'Pack total') + '</span><span>' + formatMoney(subtotal) + '</span></div>' +
            giftHtml +
            '<button type="button" class="lt-cd__quietlink" data-lt-remove-pack>' + LT('remove_pack_button', 'Remove pack') + '</button>' +
          '</div>';
      }

      return {
        syncing: syncing,
        countText: '· ' + units + ' ' + (units === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items')) + (journal ? LT('journal_suffix', ' + journal') : ''),
        // footer + nudge travel together as one replaceable zone (see
        // data-lt-packfoot-zone below) — a tier crossing can add/drop the
        // whole discount row or the whole nudge block, not just its text.
        zoneHtml: footHtml + (nudge
          ? '<div class="lt-cd__nudge"><span>' + LT('nudge_message', 'Add {n} more {noun} for {pct}% off')
              .replace('{n}', nudge.need)
              .replace('{noun}', nudge.need === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items'))
              .replace('{pct}', nudge.pct) + '</span>' +
            '<a href="' + escapeHtml(editHref) + '">' + LT('open_builder_link', 'Open the builder →') + '</a></div>'
          : '')
      };
    }

    function packCardHtml(pack) {
      var items = packItems(pack);
      var editHref = cfg.builderUrl + '?edit=' + encodeURIComponent(pack.token);
      var parts = packFootParts(pack, editHref);
      return (
        '<div class="lt-cd__pack' + (parts.syncing ? ' is-syncing' : '') + '" data-lt-pack-card data-token="' + escapeHtml(pack.token) + '">' +
          '<div class="lt-cd__packhead">' +
            '<span class="lt-cd__packkicker">' + LT('pack_kicker', 'Custom pack') + '</span>' +
            '<span class="lt-cd__packcount" data-lt-packcount>' + parts.countText + '</span>' +
            '<a class="lt-cd__packedit" href="' + escapeHtml(editHref) + '">' + LT('edit_pack', 'Edit pack') + '</a>' +
          '</div>' +
          '<div class="lt-cd__packrows">' + items.map(function (it) { return packRowHtml(pack, it); }).join('') + '</div>' +
          '<div data-lt-packfoot-zone>' + parts.zoneHtml + '</div>' +
        '</div>'
      );
    }

    // #69 P2-5: qty-tap targeted patch. Updates one row's qty/price text +
    // the pack card's count/discount/subtotal/total/nudge in place, without
    // touching any other row's DOM node (see bumpQty below for why that
    // matters: it keeps swipe state, focus, and initSwipe()'s listeners
    // intact on every row that wasn't the one tapped).
    function findRowWrap(token, variantId) {
      var wraps = Array.prototype.slice.call(el.items.querySelectorAll('[data-lt-rowwrap]'));
      return wraps.filter(function (w) { return w.dataset.token === token && w.dataset.vid === String(variantId); })[0] || null;
    }

    function patchRowQty(it, rowWrap) {
      var qty = it.quantity || 1;
      var dec = rowWrap.querySelector('[data-lt-row-dec]');
      if (dec) dec.disabled = qty <= 1;
      var qtyText = rowWrap.querySelector('[data-lt-row-qtytext]');
      if (qtyText) qtyText.textContent = qty;
      var chip = rowWrap.querySelector('[data-lt-row-chip]');
      if (chip) {
        chip.hidden = qty <= 1;
        chip.textContent = '×' + qty;
      }
      // #121 (th-build-a-pack-7): patchRowQty runs ONLY while a qty tap is
      // pending (bumpQty, before the replacePack round-trip lands) -- the row
      // is mid-optimistic-update, so its price must track the NEW qty
      // immediately. it.originalLinePrice is still the server value for the
      // OLD quantity here (that was the bug: it stayed truthy, so this line
      // never re-priced until the ~1.7s debounce+round-trip finished). Use
      // unitOriginalCents(it) -- the same undiscounted-per-unit source
      // packOriginalCents now uses -- times the NEW qty, never the stale
      // server field. packRowHtml's own (server) render formula is untouched
      // on purpose (brief #121: only the optimistic/syncing display changes).
      var priceEl = rowWrap.querySelector('[data-lt-row-price]');
      if (priceEl) priceEl.textContent = formatMoney(unitOriginalCents(it) * qty);
    }

    function patchPackTotals(pack, cardEl) {
      var editHref = cfg.builderUrl + '?edit=' + encodeURIComponent(pack.token);
      var parts = packFootParts(pack, editHref);
      cardEl.classList.toggle('is-syncing', parts.syncing);
      var countEl = cardEl.querySelector('[data-lt-packcount]');
      if (countEl) countEl.textContent = parts.countText;
      var zone = cardEl.querySelector('[data-lt-packfoot-zone]');
      if (zone) zone.innerHTML = parts.zoneHtml;
    }

    // Line-item properties on a plain cart line, mirroring the cart PAGE
    // (snippets/cart-line-items.liquid): every non-underscore property with a
    // non-blank value renders as "label: value". Underscore-prefixed keys are
    // internal (the personalised card's _lt_card_to_* recipient address, the
    // pack tokens _lt_pack / _lt_units, etc.) and are NEVER shown — the exact
    // same rule the cart page enforces with `property_first_char != '_'`.
    // Building it generically (loop over every property) is deliberate: the
    // drawer stays ignorant of any one property's name, so a personalised
    // card's "Posted to: <name>, <city>" shows up here the moment the card
    // line carries it, with no drawer-side special case to keep in sync.
    // Is this property value one of OUR uploaded files, safe to turn into a link?
    // MIRROR of the same rule in snippets/cart-line-items.liquid (the cart PAGE);
    // the two must stay in lockstep — a change here without the matching change
    // there re-opens the hole on whichever surface was missed.
    //
    // A value only links when it is provably an upload: scheme https, host our
    // own domain or Shopify's file CDN, AND the PATH (not the query string)
    // contains /uploads/. Everything else renders as escaped plain text.
    //
    // WHY the old `indexOf('/uploads/')` test was not enough (Kees audit 5, M1):
    // escaping stops HTML injection, but it does NOT make a URL SCHEME safe. A
    // hostile cart property like `javascript:alert(1)//uploads/x` contains the
    // text "/uploads/", so the old code emitted `<a href="javascript:...">`; one
    // click ran script in the storefront origin. Parsing the URL and checking
    // protocol + host + real path is what closes that. The allowlist lives
    // INSIDE the function so it travels with it (and the logic test can exercise
    // the exact shipped function in isolation).
    function isSafeUploadUrl(value) {
      var hosts = ['cdn.shopify.com', 'lettoonstore.com', 'www.lettoonstore.com', 'lettoon.myshopify.com'];
      var url;
      try { url = new URL(String(value)); } catch (e) { return false; }   // not a parseable absolute URL
      if (url.protocol !== 'https:') return false;                         // scheme must be https (kills javascript:, http:)
      if (hosts.indexOf(url.hostname.toLowerCase()) === -1) return false;  // host must be ours / Shopify's CDN
      return url.pathname.indexOf('/uploads/') !== -1;                     // and the PATH is an upload, not a query trick
    }

    function linePropertiesHtml(props) {
      if (!props) return '';
      var html = '';
      Object.keys(props).forEach(function (name) {
        if (String(name).charAt(0) === '_') return;                 // internal property — hidden
        var value = props[name];
        if (value == null || String(value).trim() === '') return;   // blank (cart page: `p.last != blank`)
        var valueHtml;
        if (isSafeUploadUrl(value)) {
          // uploaded-file property: link to the file, show just its name (cart-page parity)
          valueHtml = '<a href="' + escapeHtml(value) + '">' + escapeHtml(String(value).split('/').pop()) + '</a>';
        } else {
          valueHtml = escapeHtml(value);
        }
        html += '<div class="lt-cd__lineprop"><span>' + escapeHtml(name) + ': </span><span>' + valueHtml + '</span></div>';
      });
      return html;
    }

    function regularLineHtml(line) {
      var meta = [];
      (line.options_with_values || []).forEach(function (o) {
        if (o.value && o.value !== 'Default Title') meta.push(o.value);
      });
      var discounted = line.original_line_price > line.final_line_price;
      var price = (discounted ? '<del>' + formatMoney(line.original_line_price) + '</del>' : '') +
        formatMoney(line.final_line_price);
      // #68 finding 21: same repeated-name problem as packRowHtml above, for
      // loose (non-pack) line items.
      var lineNameAttr = escapeHtml(line.product_title || line.title);
      return (
        '<div class="lt-cd__line" data-lt-line data-key="' + escapeHtml(line.key) + '">' +
          '<div class="lt-cd__linethumb">' + imgTag(line.image, line.product_title, 128) + '</div>' +
          '<div>' +
            '<div class="lt-cd__linename"><a href="' + escapeHtml(line.url || '#') + '">' + escapeHtml(line.product_title || line.title) + '</a></div>' +
            (meta.length ? '<div class="lt-cd__linevariant">' + escapeHtml(meta.join(' · ')) + '</div>' : '') +
            linePropertiesHtml(line.properties) +
            '<div class="lt-cd__lineqty">' +
              '<button type="button" data-lt-line-dec aria-label="' + LT('fewer_of_full', 'Fewer {name}').replace('{name}', lineNameAttr) + '"' + (line.quantity <= qtyRule(line.quantity_rule).min ? ' disabled' : '') + '>−</button>' +
              // #176: typeable box instead of a bare number. type=text + inputmode=numeric
              // (not type=number) so there are no browser spinners and Enter/blur stay ours;
              // the digits-only filter and the snap to quantity_rule live in handleLineQtyEvent.
              // The aria-label reuses the existing "Quantity of {name} now {qty}" string.
              '<input type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" class="lt-cd__lineqtyval" data-lt-line-qty value="' + line.quantity + '" aria-label="' + LT('quantity_of_full', 'Quantity of {name} now {qty}').replace('{name}', lineNameAttr).replace('{qty}', line.quantity) + '">' +
              '<button type="button" data-lt-line-inc aria-label="' + LT('more_of_full', 'More {name}').replace('{name}', lineNameAttr) + '">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="lt-cd__lineend">' +
            '<span class="lt-cd__lineprice">' + price + '</span>' +
            '<button type="button" class="lt-cd__quietlink lt-cd__quietlink--terra" data-lt-line-remove aria-label="' + LT('remove_of_full', 'Remove {name}').replace('{name}', lineNameAttr) + '">' + LT('remove_text', 'Remove') + '</button>' +
          '</div>' +
        '</div>'
      );
    }

    function renderShip() {
      var subtotal = state.cart ? state.cart.total_price : 0;
      var isEmpty = !state.cart || state.cart.item_count === 0;
      if (cfg.geo === 'OTHER' || cfg.threshold <= 0 || isEmpty) {
        el.ship.hidden = true;
        el.shipnote.textContent = LT('shipping_note', 'Shipping calculated at checkout');
        return;
      }
      var t = cfg.threshold;
      if (subtotal < t) {
        el.ship.hidden = false;
        el.shipBelow.hidden = false;
        el.shipReached.hidden = true;
        el.shipAway.textContent = formatMoney(t - subtotal);
        el.shipFill.style.width = Math.min(100, subtotal / t * 100).toFixed(1) + '%';
        el.shipnote.textContent = LT('shipping_note', 'Shipping calculated at checkout');
      } else if (subtotal < t + 2000) {
        // just reached: quiet sage line, no flourish
        el.ship.hidden = false;
        el.shipBelow.hidden = true;
        el.shipReached.hidden = false;
        el.shipnote.textContent = cfg.geo === 'NL' ? LT('ships_free_nl', 'Ships free to the Netherlands ✓') : LT('ships_free_eu', 'Ships free across the EU ✓');
      } else {
        // far above: the module disappears entirely — no noise
        el.ship.hidden = true;
        el.shipnote.textContent = cfg.geo === 'NL' ? LT('ships_free_nl', 'Ships free to the Netherlands ✓') : LT('ships_free_eu', 'Ships free across the EU ✓');
      }
    }

    function renderMystery() {
      if (!el.mystery) return; // setting off -> block not rendered
      var isEmpty = !state.cart || state.cart.item_count === 0;
      el.mystery.hidden = isEmpty;
      if (isEmpty) return;
      var line = mysteryLine();
      el.mysteryOff.hidden = !!line;
      el.mysteryOn.hidden = !line;
    }

    // The header count text + the cart badge. Split out of render() (#176 fix
    // round) so a typed quantity commit can refresh just these two in place.
    function renderCount() {
      var count = displayCount();
      el.count.textContent = '· ' + count + ' ' + (count === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items'));
      setBadge(count); // header badge tracks the drawer count, optimistically too
    }

    // #176 fix round (F2): render() replaces every line's DOM, which silently
    // drops keyboard focus to <body>. For the loose-line controls (box, -, +,
    // Remove) remember which control of which line had focus and give focus
    // back to the SAME control in the new DOM. Only ever acts when focus is
    // already inside the items list, so it can never steal focus from elsewhere.
    var LINE_FOCUS_ATTRS = ['data-lt-line-qty', 'data-lt-line-dec', 'data-lt-line-inc', 'data-lt-line-remove'];
    function captureLineFocus() {
      var a = document.activeElement;
      if (!a || !a.closest || !el.items.contains(a)) return null;
      var lineEl = a.closest('[data-lt-line]');
      if (!lineEl) return null;
      var all = Array.prototype.slice.call(el.items.querySelectorAll('[data-lt-line]'));
      for (var i = 0; i < LINE_FOCUS_ATTRS.length; i++) {
        if (a.hasAttribute(LINE_FOCUS_ATTRS[i])) return { key: lineEl.dataset.key, index: all.indexOf(lineEl), count: all.length, attr: LINE_FOCUS_ATTRS[i] };
      }
      return null;
    }
    // Match the line by key; a line key can change on a rebuild (the preview
    // fixture churns them on purpose), so fall back to the same position, but
    // only when the number of lines is unchanged: if the line is GONE (Remove)
    // focus must not jump onto a neighbour's Remove button.
    function restoreLineFocus(f) {
      if (!f) return;
      var lines = el.items.querySelectorAll('[data-lt-line]');
      var lineEl = null;
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].dataset.key === f.key) { lineEl = lines[i]; break; }
      }
      if (!lineEl && lines.length === f.count) lineEl = lines[f.index] || null;
      var target = lineEl && lineEl.querySelector('[' + f.attr + ']');
      if (target && !target.disabled) target.focus({ preventScroll: true });
    }

    function render() {
      var isEmpty = !state.cart || state.cart.item_count === 0;

      el.empty.hidden = !isEmpty;
      el.items.hidden = isEmpty;
      el.footer.hidden = isEmpty;

      if (!isEmpty) {
        // #18 r3e option A: the just-added LOOSE line (identified by the add
        // event's payload, see state.justAdded) is pinned above the pack block.
        // Grouping is otherwise untouched: packs first (newest pack on top via
        // cart order), then plain rows. Match by line key first; fall back to
        // variant id so an add that merged into an existing line still pins.
        var regs = regularLines();
        var pinnedHtml = '';
        if (state.justAdded) {
          var pinIdx = -1, i;
          if (state.justAdded.key) {
            for (i = 0; i < regs.length; i++) {
              if (regs[i].key === state.justAdded.key) { pinIdx = i; break; }
            }
          }
          if (pinIdx === -1 && state.justAdded.variantId != null) {
            for (i = 0; i < regs.length; i++) {
              if (String(regs[i].variant_id) === String(state.justAdded.variantId)) { pinIdx = i; break; }
            }
          }
          if (pinIdx !== -1) pinnedHtml = regularLineHtml(regs.splice(pinIdx, 1)[0]);
        }
        var html = pinnedHtml + state.packs.map(packCardHtml).join('') + regs.map(regularLineHtml).join('');
        var keepFocus = captureLineFocus();
        el.items.innerHTML = html;
        restoreLineFocus(keepFocus);
        renderCount();
        el.subtotal.textContent = formatMoney(state.cart.total_price);
        el.checkoutTotal.textContent = formatMoney(state.cart.total_price);
        initSwipe();
        initPeek();
      } else {
        el.items.innerHTML = '';
        el.count.textContent = '';
        if (state.cart) setBadge(0);
      }

      renderShip();
      renderMystery();
    }

    function showError(msg) {
      el.error.textContent = msg;
      el.error.hidden = false;
      window.clearTimeout(showError._t);
      showError._t = window.setTimeout(function () { el.error.hidden = true; }, 5000);
    }

    /* ------------------------------------------------------- data loading */

    function refresh() {
      state.loading = true;
      return Promise.all([fetchCart(), getPacks()]).then(function (res) {
        state.cart = res[0];
        state.packs = res[1];
        state.stale = false;
        state.loading = false;
        render();
      }).catch(function () {
        state.loading = false;
        showError(LT('err_load_cart', 'Couldn’t load your cart. Please try again.'));
      });
    }

    /* ----------------------------------------------------- open and close */

    function openDrawer() {
      if (state.open) return;
      state.open = true;
      state.lastFocus = document.activeElement;
      root.classList.add('lt-cd--open');
      root.setAttribute('aria-hidden', 'false');
      root.inert = false; // close-button/links must become focusable the moment the drawer opens
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: el.items }));
      if (state.stale) refresh(); else render();
      window.setTimeout(function () {
        if (el.close) el.close.focus({ preventScroll: true });
      }, 260);
      window.theme = window.theme || {};
      window.theme.state = window.theme.state || {};
      window.theme.state.cartOpen = true;
    }

    function closeDrawer() {
      if (!state.open) return;
      state.open = false;
      state.justAdded = null; // the pin lives for one drawer session only
      root.classList.remove('lt-cd--open');
      root.setAttribute('aria-hidden', 'true');
      root.inert = true; // synchronously un-focus the close button/links (Lighthouse aria-hidden-focus)
      closeEditSheet();
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      if (window.theme && window.theme.state) window.theme.state.cartOpen = false;
      if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus({ preventScroll: true });
    }

    root.addEventListener('theme:drawer:open', openDrawer);
    root.addEventListener('theme:drawer:close', closeDrawer);
    root.addEventListener('theme:drawer:toggle', function () {
      if (state.open) closeDrawer(); else openDrawer();
    });

    // hand the module-scope capture delegation (bound at script evaluation,
    // above initDrawer) its entry points, and honor a click that raced boot
    drawerApi = {
      open: openDrawer,
      close: closeDrawer,
      toggle: function () { if (state.open) closeDrawer(); else openDrawer(); }
    };
    if (pendingDrawerOpen) {
      pendingDrawerOpen = false;
      openDrawer();
    }

    el.scrim.addEventListener('click', closeDrawer);
    el.close.addEventListener('click', closeDrawer);
    document.addEventListener('keyup', function (e) {
      // #68 finding 28 (P3, same lines as the finding-15 trap fix): e.code
      // is layout-dependent (a non-US keyboard's Escape key can report a
      // different code); e.key is the value the other two files already use.
      if (e.key !== 'Escape' || !state.open) return;
      if (state.sheet) { closeEditSheet(); return; }
      closeDrawer();
    });

    // rudimentary focus trap: Tab cycles within the panel while open, and
    // within the edit sheet/dialog while THAT is open (it sits outside the
    // panel in the DOM, so the panel-scoped query would let focus escape)
    document.addEventListener('keydown', function (e) {
      if (!state.open || e.key !== 'Tab') return;
      var container = state.sheet ? el.editSheet : el.panel;
      // #68 finding 15: an empty cart hides the items list and shows
      // .lt-cd__emptystate instead — the querySelectorAll below still
      // matched buttons/links sitting inside OTHER [hidden] regions (e.g.
      // the footer's checkout button when the cart is empty), so `first`/
      // `last` could resolve to an element focus can never actually reach,
      // breaking the wrap. offsetParent is null for anything display:none
      // (including a hidden ancestor), so this filters to what's really
      // tabbable right now.
      var focusables = Array.prototype.slice.call(
        container.querySelectorAll('button:not([disabled]), [href], input, select, [tabindex]:not([tabindex="-1"])')
      ).filter(function (node) { return node.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // add-to-cart elsewhere (custom.js re-fetches the cart and fires this with
    // its own detail) — our own dispatches are tagged source:'lt-pack-cart'
    // and are ignored here because every mutation path already refreshes.
    // When the dispatcher identifies the added line (detail.added = { key,
    // variantId }, derived from its /cart/add.js response — never from
    // timestamps), that line is pinned above the pack block on the renders of
    // this drawer session (#18 r3e option A). Events without `added` (foreign
    // mutations) don't clear an existing pin: the theme's own updateHeaderTotal
    // fires an added-less change alongside every stock add, and letting it
    // race the pin away would break the feature on those paths. The pin dies
    // deterministically on drawer close instead.
    document.addEventListener('theme:cart:change', function (e) {
      if (e.detail && e.detail.source === 'lt-pack-cart') return;
      if (e.detail && e.detail.added) state.justAdded = e.detail.added;
      state.stale = true;
      if (e.detail && e.detail.cart) state.cart = e.detail.cart;
      if (state.open) refresh();
    });

    /* --------------------------------------------- pack mutations (C4 use) */

    function payloadFromPack(pack) {
      // rebuild the C1 items array for replacePack — includes the journal line
      // (teardown removes it, so it must ride along or it would be lost)
      return (pack.items || []).map(function (it) {
        return {
          id: parseInt(it.variantId, 10),
          quantity: it.quantity || 1,
          properties: it.properties || (function () {
            var p = {};
            p[PACK_PROP] = pack.token;
            p[ROLE_PROP] = it.role || 'item';
            return p;
          })()
        };
      });
    }

    function flushPack(token, immediate) {
      window.clearTimeout(state.flushTimers[token]);
      var run = function () {
        delete state.flushTimers[token];
        var pack = findPack(token);
        if (!pack) return;
        var gen = state.flushGen[token] || 0;
        var items = payloadFromPack(pack);
        var op = packItems(pack).length === 0
          ? (items.length ? replacePack(token, items) : removePack(token))
          : replacePack(token, items);
        var settle = function (failed) {
          // a tap that landed while this round-trip was in flight bumped the
          // generation and scheduled its own flush — keep the optimistic
          // render and let THAT flush do the authoritative refresh
          if ((state.flushGen[token] || 0) !== gen) return;
          delete state.syncTokens[token];
          if (failed) showError(LT('err_update_pack', 'Couldn’t update your pack. Please try again.'));
          refresh();
        };
        op.then(function () { settle(false); }).catch(function () { settle(true); });
      };
      if (immediate) run();
      else state.flushTimers[token] = window.setTimeout(run, 500); // coalesce rapid taps
    }

    function bumpQty(token, variantId, delta) {
      var pack = findPack(token);
      if (!pack) return;
      var it = findItem(pack, variantId);
      if (!it) return;
      var next = Math.max(1, (it.quantity || 1) + delta);
      if (next === it.quantity) return;
      it.quantity = next;
      state.syncTokens[token] = true;
      state.flushGen[token] = (state.flushGen[token] || 0) + 1;
      announce(LT('quantity_of_full', 'Quantity of {name} now {qty}').replace('{name}', it.name).replace('{qty}', next));
      // #69 P2-5: a qty tap is a pure quantity change — no row is added or
      // removed, so patch this row + this pack's totals in place instead of
      // render()'s full innerHTML rebuild (which recreates every row in the
      // drawer and re-runs initSwipe() on all of them, just to update one
      // number). Falls back to the full render() if the row isn't in the DOM
      // for some reason — never silently skips the update.
      var rowWrap = findRowWrap(token, variantId);
      var cardEl = rowWrap && rowWrap.closest('[data-lt-pack-card]');
      if (rowWrap && cardEl) {
        patchRowQty(it, rowWrap);
        patchPackTotals(pack, cardEl);
        var count = displayCount();
        el.count.textContent = '· ' + count + ' ' + (count === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items'));
        setBadge(count); // header badge tracks the drawer count, optimistically too — same as render()
      } else {
        render();
      }
      flushPack(token, false);
    }

    function removeItem(token, variantId) {
      var pack = findPack(token);
      if (!pack) return;
      var removed = findItem(pack, variantId); // #68 finding 16: name it before it's filtered out
      pack.items = (pack.items || []).filter(function (it) {
        return !(String(it.variantId) === String(variantId) && it.role !== 'journal');
      });
      state.syncTokens[token] = true;
      state.flushGen[token] = (state.flushGen[token] || 0) + 1;
      if (removed) announce(LT('removed_from_pack_full', '{name} removed from your pack').replace('{name}', removed.name));
      if (packItems(pack).length === 0) {
        state.packs = state.packs.filter(function (p) { return p.token !== token; });
        render();
        removePack(token).then(refresh).catch(function () {
          showError(LT('err_update_pack', 'Couldn’t update your pack. Please try again.'));
          refresh();
        });
        return;
      }
      render();
      flushPack(token, true);
    }

    function removeWholePack(token) {
      state.packs = state.packs.filter(function (p) { return p.token !== token; });
      announce(LT('removed_pack', 'Pack removed from your cart'));
      render();
      removePack(token).then(refresh).catch(function () {
        showError(LT('err_remove_pack', 'Couldn’t remove the pack. Please try again.'));
        refresh();
      });
    }

    // #62: removes ONLY the journal line (by its real cart key, looked up
    // fresh from state.cart), never touches the pack's item lines. Plain
    // network round-trip + refresh, same shape as removeMystery — no
    // optimistic pre-render because the journal's presence is derived from
    // state.packs/state.cart together (packJournal + findPack), not a
    // simple local list this function could trim in place.
    function removeJournal(token) {
      var line = journalLineForToken(token);
      if (!line) return;
      announce(LT('removed_name', '{name} removed').replace('{name}', line.product_title || line.title || LT('item_fallback_name', 'Item')));
      changeLine(line.key, 0).then(refresh).catch(function () {
        showError(LT('err_remove_item', 'Couldn’t remove that item. Please try again.'));
        refresh();
      });
    }

    /* -------------------------------------------------- plain line mutations */

    /* ------------------------------------------- quantity rule (#176) */

    // Shopify puts the buyer's quantity rule on every /cart.js line item:
    // quantity_rule { min, max, increment } (max null = unlimited). For a
    // consumer it is always 1/null/1; a wholesale (B2B) buyer can have
    // e.g. min 5. A line without the field (or with garbage in it) falls back
    // to the consumer defaults, so the drawer never blocks on missing data.
    function qtyRule(rule) {
      var r = rule || {};
      var min = parseInt(r.min, 10);
      var max = parseInt(r.max, 10);
      var inc = parseInt(r.increment, 10);
      return {
        min: min > 0 ? min : 1,
        max: max > 0 ? max : null,
        increment: inc > 0 ? inc : 1
      };
    }

    // Snap any typed/stepped number onto what Shopify will accept:
    // not a number -> min; below min -> min; off the min+k*increment grid ->
    // the next grid point up; above max -> the highest grid point <= max.
    // Done HERE, before /cart/change.js, because Shopify answers a rule
    // violation with an error and the line would silently keep its old qty.
    function snapQty(n, rule) {
      var r = qtyRule(rule);
      n = Math.floor(Number(n));
      if (!isFinite(n) || n < r.min) return r.min;
      var snapped = r.min + Math.ceil((n - r.min) / r.increment) * r.increment;
      if (r.max !== null && snapped > r.max) {
        snapped = r.min + Math.floor((r.max - r.min) / r.increment) * r.increment;
      }
      return snapped;
    }

    // inPlace (#176 fix round, F1): a typed commit happens on blur, i.e. BETWEEN
    // mouse-down and click of whatever the buyer is pressing next (-, +, Remove).
    // Redrawing the line there swaps that button out and the click is lost. So a
    // typed commit patches the existing elements instead (patchLineQty); the
    // normal full render still follows when the server answers, as after a +.
    function changeRegularLine(key, qty, inPlace) {
      var line = (state.cart.items || []).filter(function (l) { return l.key === key; })[0];
      if (line) {
        // #68 finding 16: composed announcement for the loose-line stepper/remove
        var name = line.product_title || line.title;
        announce(qty === 0 ? LT('removed_from_cart_full', '{name} removed from your cart').replace('{name}', name) : LT('quantity_of_full', 'Quantity of {name} now {qty}').replace('{name}', name).replace('{qty}', qty));
        line.quantity = qty;
        if (inPlace) patchLineQty(line); else render(); // optimistic count/stepper
      }
      changeLine(key, qty).then(refresh).catch(function (err) {
        var msg = (err && err.json && err.json.message) || LT('err_update_cart', 'Couldn’t update your cart. Please try again.');
        showError(msg);
        refresh();
      });
    }

    // Same optimistic update render() does for a quantity change (box value,
    // minus enabled/disabled, header count + badge), but on the EXISTING
    // elements. Price and subtotal stay as the server last said, exactly as
    // before: render() never recomputed those optimistically either; the
    // server answer (refresh) updates them.
    function patchLineQty(line) {
      var lines = el.items.querySelectorAll('[data-lt-line]');
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].dataset.key !== line.key) continue;
        var input = lines[i].querySelector('[data-lt-line-qty]');
        if (input) {
          input.value = line.quantity;
          input.setAttribute('aria-label', LT('quantity_of_full', 'Quantity of {name} now {qty}').replace('{name}', line.product_title || line.title).replace('{qty}', line.quantity));
        }
        var dec = lines[i].querySelector('[data-lt-line-dec]');
        if (dec) dec.disabled = line.quantity <= qtyRule(line.quantity_rule).min;
        break;
      }
      renderCount();
    }

    // What quantity should this loose line get after a gesture? Always lands on
    // the line's quantity_rule grid (see snapQty). inc/dec move by the rule's
    // increment; 'type' snaps whatever the buyer typed.
    function resolveLineQty(line, action, typed) {
      var r = qtyRule(line.quantity_rule);
      if (action === 'inc') return snapQty(line.quantity + r.increment, r);
      if (action === 'dec') return snapQty(line.quantity - r.increment, r);
      return snapQty(typed, r);
    }

    function findRegularLine(key) {
      return (state.cart.items || []).filter(function (l) { return l.key === key; })[0];
    }

    // - / + buttons. No request when the step would not change anything
    // (already at min or max), same as the old Math.max(1, ...) guard.
    function stepLineQty(key, action) {
      var line = findRegularLine(key);
      if (!line) return;
      var qty = resolveLineQty(line, action);
      if (qty !== line.quantity) changeRegularLine(key, qty);
    }

    // A typed quantity was committed (blur or Enter). ALWAYS goes through
    // changeRegularLine, even when the snap lands back on the current number
    // (typing 3 on a min-5 line): the optimistic render then resets the box to
    // the real value and the live-region announces it.
    function commitLineQty(key, raw) {
      var line = findRegularLine(key);
      if (!line) return;
      changeRegularLine(key, resolveLineQty(line, 'type', raw), true);
    }

    // One listener entry point for the quantity box (bound on el.items for
    // input / change / keydown). `change` is the only place that commits: the
    // browser fires it on blur, and Enter just blurs the box. That way Enter can
    // never double-fire with a later blur, and it never submits a form or
    // reloads the page (preventDefault). After Enter's blur (= the commit) the
    // focus goes straight back into the box (#176 fix round, F2): the buyer
    // pressed Enter to confirm, not to leave. Focus then sees the committed
    // number as its starting text, so leaving later does not commit twice.
    function handleLineQtyEvent(e) {
      var input = e.target && e.target.closest && e.target.closest('[data-lt-line-qty]');
      if (!input) return;
      if (e.type === 'input') {
        // digits only: no letters, minus signs or decimals ever reach the snap
        var digits = input.value.replace(/\D/g, '');
        if (digits !== input.value) input.value = digits;
      } else if (e.type === 'keydown') {
        if (e.key === 'Enter') {
          e.preventDefault();
          input.blur();
          if (input.focus) input.focus({ preventScroll: true });
        }
      } else if (e.type === 'change') {
        var lineEl = input.closest('[data-lt-line]');
        if (lineEl) commitLineQty(lineEl.dataset.key, input.value);
      }
    }

    function addMystery() {
      addLine(cfg.mysteryVariant, 1).then(refresh).catch(function () {
        showError(LT('err_add_mystery', 'Couldn’t add the mystery item. Please try again.'));
      });
    }

    function removeMystery() {
      var line = mysteryLine();
      if (!line) return;
      announce(LT('removed_mystery', 'Mystery item removed'));
      changeLine(line.key, 0).then(refresh).catch(function () {
        showError(LT('err_remove_mystery', 'Couldn’t remove the mystery item. Please try again.'));
      });
    }

    /* --------------------------------------------------- mobile edit sheet */

    function openEditSheet(token, variantId) {
      var pack = findPack(token);
      var it = pack && findItem(pack, variantId);
      if (!it) return;
      var selected = {};
      (it.options || []).forEach(function (o) { selected[o.name] = o.selected; });
      state.sheet = { token: token, variantId: variantId, qty: it.quantity || 1, selected: selected };
      el.editThumb.innerHTML = imgTag(it.image, it.name, 104);
      el.editName.textContent = it.name;
      var sub = typeLabel(it.type);
      el.editSub.textContent = sub + (sub ? ' · ' : '') + LT('price_each', '{price} each').replace('{price}', formatMoney(it.price || 0));
      renderEditSheet(it);
      state.sheetLastFocus = document.activeElement;
      el.edit.hidden = false;
      if (el.editClose) el.editClose.focus({ preventScroll: true });
    }

    function renderEditSheet(it) {
      var s = state.sheet;
      el.editQty.textContent = s.qty;
      el.editDec.disabled = s.qty <= 1;
      // one pill row per REAL product option; products with no options
      // (183 in the catalog) simply get no rows
      var rows = (it.options || []).map(function (o) {
        var pills = (o.values || []).map(function (v) {
          var sel = s.selected[o.name] === v;
          // a value is offered only if some variant with the OTHER current
          // selections carries it
          var candidate = variantForSelection(it, mergeSel(s.selected, o.name, v));
          var disabled = !candidate;
          return '<button type="button" class="lt-cd__editpill' + (sel ? ' is-selected' : '') + '"' +
            ' data-lt-pill data-opt="' + escapeHtml(o.name) + '" data-val="' + escapeHtml(v) + '"' +
            (disabled ? ' disabled' : '') + '>' + escapeHtml(v) + '</button>';
        }).join('');
        return '<div class="lt-cd__editrow"><span class="lt-cd__editlabel">' + escapeHtml(o.name) + '</span>' +
          '<div class="lt-cd__editpills">' + pills + '</div></div>';
      }).join('');
      el.editOptions.innerHTML = rows;
    }

    function mergeSel(selected, name, value) {
      var out = {};
      Object.keys(selected).forEach(function (k) { out[k] = selected[k]; });
      out[name] = value;
      return out;
    }

    function variantForSelection(it, selected) {
      var names = (it.options || []).map(function (o) { return o.name; });
      return (it.variants || []).filter(function (v) {
        if (v.available === false) return false;
        return names.every(function (n, idx) {
          // variant.options is position-ordered to the product's options; our
          // options array preserves that order (built in product option order)
          return String(v.options[idx]) === String(selected[n]);
        });
      })[0] || null;
    }

    function closeEditSheet() {
      var wasOpen = !!state.sheet;
      var target = state.sheetLastFocus;
      state.sheet = null;
      state.sheetLastFocus = null;
      el.edit.hidden = true;
      if (!wasOpen) return;
      if (target && target.focus && document.contains(target)) {
        target.focus({ preventScroll: true });
      }
      // the desktop row Edit button is hover-only: if the restore target can't
      // take focus (display:none without hover), keep focus inside the drawer
      if (state.open && document.activeElement !== target) {
        el.close.focus({ preventScroll: true });
      }
    }

    function applyEditSheet() {
      var s = state.sheet;
      if (!s) return;
      var pack = findPack(s.token);
      var it = pack && findItem(pack, s.variantId);
      if (!it) { closeEditSheet(); return; }
      var changed = false;
      if ((it.quantity || 1) !== s.qty) { it.quantity = s.qty; changed = true; }
      if ((it.options || []).length) {
        var v = variantForSelection(it, s.selected);
        if (v && String(v.id) !== String(it.variantId)) {
          it.variantId = v.id;
          it.price = v.price;
          it.title = v.title;
          (it.options || []).forEach(function (o) { o.selected = s.selected[o.name]; });
          changed = true;
        }
      }
      closeEditSheet();
      if (changed) {
        state.syncTokens[s.token] = true;
        state.flushGen[s.token] = (state.flushGen[s.token] || 0) + 1;
        render();
        flushPack(s.token, true); // one round-trip for the whole sheet
        // render() rebuilt the rows, so the restored focus target may be gone
        if (document.activeElement === document.body) {
          el.close.focus({ preventScroll: true });
        }
      }
    }

    el.editScrim.addEventListener('click', closeEditSheet);
    el.editClose.addEventListener('click', closeEditSheet);
    el.editDec.addEventListener('click', function () {
      if (!state.sheet || state.sheet.qty <= 1) return;
      state.sheet.qty -= 1;
      refreshSheet();
    });
    el.editInc.addEventListener('click', function () {
      if (!state.sheet) return;
      state.sheet.qty += 1;
      refreshSheet();
    });
    el.editOptions.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-lt-pill]');
      if (!pill || !state.sheet) return;
      state.sheet.selected[pill.dataset.opt] = pill.dataset.val;
      refreshSheet();
    });
    el.editDone.addEventListener('click', applyEditSheet);

    function refreshSheet() {
      var s = state.sheet;
      if (!s) return;
      var pack = findPack(s.token);
      var it = pack && findItem(pack, s.variantId);
      if (it) renderEditSheet(it);
    }

    /* -------------------------------------------------- items area clicks */

    el.items.addEventListener('click', function (e) {
      var t = e.target;
      var journalWrap = t.closest && t.closest('[data-lt-journal-row]');
      if (journalWrap && t.closest('[data-lt-journal-remove]')) {
        return removeJournal(journalWrap.dataset.token);
      }
      var wrap = t.closest && t.closest('[data-lt-rowwrap]');
      if (wrap) {
        var token = wrap.dataset.token;
        var vid = wrap.dataset.vid;
        if (t.closest('[data-lt-row-inc]')) return bumpQty(token, vid, 1);
        if (t.closest('[data-lt-row-dec]')) return bumpQty(token, vid, -1);
        if (t.closest('[data-lt-row-remove]')) return removeItem(token, vid);
        if (t.closest('[data-lt-row-edit]')) return openEditSheet(token, vid);
      }
      var card = t.closest && t.closest('[data-lt-pack-card]');
      if (card && t.closest('[data-lt-remove-pack]')) return removeWholePack(card.dataset.token);

      var lineEl = t.closest && t.closest('[data-lt-line]');
      if (lineEl) {
        var key = lineEl.dataset.key;
        var line = (state.cart.items || []).filter(function (l) { return l.key === key; })[0];
        if (!line) return;
        if (t.closest('[data-lt-line-inc]')) return stepLineQty(key, 'inc');
        if (t.closest('[data-lt-line-dec]')) return stepLineQty(key, 'dec');
        if (t.closest('[data-lt-line-remove]')) return changeRegularLine(key, 0);
      }
    });

    // #176: typeable quantity box on loose lines
    ['input', 'change', 'keydown'].forEach(function (type) {
      el.items.addEventListener(type, handleLineQtyEvent);
    });

    if (el.mystery) {
      root.querySelector('[data-lt-cd-mystery-add]').addEventListener('click', addMystery);
      root.querySelector('[data-lt-cd-mystery-remove]').addEventListener('click', removeMystery);
    }

    /* ------------------------------------------------- mobile swipe reveal */

    // iOS-style swipe-left on pack rows: reveal Edit (sand) + Remove (terra),
    // snap open past ~half (70px of 140px), else snap shut. The action layer
    // is visibility:hidden at rest (classes is-dragging/is-open/is-peeking).
    var swipe = { wrap: null, startX: 0, startY: 0, base: 0, dx: 0, active: false, row: null };

    function closeOpenRows(except) {
      el.items.querySelectorAll('.lt-cd__rowwrap.is-open').forEach(function (w) {
        if (w !== except) {
          w.classList.remove('is-open');
          var r = w.querySelector('[data-lt-row]');
          if (r) r.style.transform = '';
        }
      });
    }

    function initSwipe() {
      if (!isMobile()) return;
      el.items.querySelectorAll('[data-lt-rowwrap]').forEach(function (wrap) {
        var row = wrap.querySelector('[data-lt-row]');
        if (!row) return;
        row.addEventListener('pointerdown', function (e) {
          cancelPeek();
          swipe.wrap = wrap;
          swipe.row = row;
          swipe.startX = e.clientX;
          swipe.startY = e.clientY;
          swipe.base = wrap.classList.contains('is-open') ? -140 : 0;
          swipe.dx = swipe.base;
          swipe.active = false; // becomes true after horizontal intent
        });
        // #69 P2-8: passive — this handler never calls preventDefault, so the
        // browser doesn't need to wait for it before deciding how to scroll.
        row.addEventListener('pointermove', function (e) {
          if (swipe.wrap !== wrap) return;
          var dx = e.clientX - swipe.startX;
          var dy = e.clientY - swipe.startY;
          if (!swipe.active) {
            if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return; // vertical scroll wins
            swipe.active = true;
            wrap.classList.add('is-dragging');
            try { row.setPointerCapture(e.pointerId); } catch (err) { /* older engines */ }
          }
          swipe.dx = Math.max(-150, Math.min(6, swipe.base + dx));
          row.style.transform = 'translateX(' + swipe.dx + 'px)';
        }, { passive: true });
        function up() {
          if (swipe.wrap !== wrap) return;
          var wasActive = swipe.active;
          var dx = swipe.dx;
          wrap.classList.remove('is-dragging');
          row.style.transform = '';
          if (wasActive) {
            if (dx < -70) {
              closeOpenRows(wrap);
              wrap.classList.add('is-open');
            } else {
              wrap.classList.remove('is-open');
            }
          }
          swipe.wrap = null;
          swipe.row = null;
          swipe.active = false;
        }
        row.addEventListener('pointerup', up);
        row.addEventListener('pointercancel', up);
      });
    }

    /* ------------------------------------------------ one-time peek teach */

    var PEEK_KEY = 'lt-cd-peek-done';

    function cancelPeek() {
      var peeking = el.items.querySelector('.lt-cd__rowwrap.is-peeking');
      if (peeking) peeking.classList.remove('is-peeking');
      try { sessionStorage.setItem(PEEK_KEY, '1'); } catch (e) { /* private mode */ }
    }

    function initPeek() {
      if (!isMobile()) return;
      var done = false;
      try { done = sessionStorage.getItem(PEEK_KEY) === '1'; } catch (e) { done = true; }
      if (done) return;
      var first = el.items.querySelector('[data-lt-rowwrap]');
      if (!first) return;
      first.classList.add('is-peeking');
      var row = first.querySelector('[data-lt-row]');
      if (row) {
        row.addEventListener('animationend', function () {
          first.classList.remove('is-peeking');
          try { sessionStorage.setItem(PEEK_KEY, '1'); } catch (e) { /* ignore */ }
        }, { once: true });
      }
    }

    /* --------------------------------------------------------------- boot */
    // Nothing to do eagerly: state starts stale and the first theme:drawer:open
    // (header icon, add-to-cart via custom.js, builder add) triggers refresh().
  }

  function boot() {
    initBadge();
    initDrawer();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
